import { css } from "@emotion/react";
import type { CSSInterpolation } from "@emotion/serialize"; // necessary for css prop to work
import styled from "@emotion/styled";
import React from "react";

import { DonationFlowPaymentOption } from "@every.org/common/src/entity/types";
import {
  ClientRouteName,
  DonateModalUrlParams,
  getRoutePath,
  URLFormat,
} from "@every.org/common/src/helpers/clientRoutes";

import {
  Button,
  ButtonRole,
  ButtonSize,
  ButtonTargetKind,
} from "src/components/Button";
import { CloudinaryImg } from "src/components/CloudinaryImg";
import { PageSection } from "src/components/layout/PageSection";
import { SectionAnchor } from "src/pages/Legal/TermsOfService";
import { FUND_ACCOUNT_FRAGMENT_ID } from "src/pages/Manage/PaymentPage";
import { cssForMediaSize, MediaSize } from "src/theme/mediaQueries";
import { spacing, verticalStackCss } from "src/theme/spacing";

const STARS_AND_BUTTON_IMAGE_CLOUDINARY_ID = "stars_and_button_wuf9d9";
const STARS_AND_BUTTON_IMAGE_HEIGHT = 200;
const LOGOS_IMAGE_CLOUDINARY_ID = "crypto_logos_ring_ynrabq";
const LOGOS_IMAGE_SIZE = 180;

const pageSectionCss = css`
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  padding-bottom: ${spacing.xxxxl};
  gap: ${spacing.xxl};
  ${cssForMediaSize({
    min: MediaSize.MEDIUM,
    css: css`
      grid-template-columns: 1fr 1fr;
    `,
  })}
`;

const GridCell = styled.div`
  ${verticalStackCss.l};
  align-items: center;
  text-align: center;
  justify-content: center;
`;

const TextContainer = styled.div`
  ${verticalStackCss.m};
  max-width: 496px;
`;

export const GetButtonText: React.FCC = () => (
  <React.Fragment>
    <TextContainer>
      <SectionAnchor id="donate-button" />
      <h3>Get a donate crypto link or button</h3>
      <p>
        {
          "Want to add a donate crypto button to your nonprofit's website? Or a link for your favorite nonprofit to share on social or email and influence others to give?"
        }
      </p>
    </TextContainer>
    <Button
      data-tname="CreateLinkOrButton__Link"
      role={ButtonRole.PRIMARY}
      size={ButtonSize.MEDIUM}
      onClick={{
        kind: ButtonTargetKind.LINK,
        to: getRoutePath({
          name: ClientRouteName.BUTTON_NONPROFIT_SEARCH,
          format: URLFormat.RELATIVE,
          query: {
            [DonateModalUrlParams.METHOD]: DonationFlowPaymentOption.CRYPTO,
          },
        }),
      }}
    >
      Create link or button
    </Button>
  </React.Fragment>
);

export const CryptoLogosImg: React.FCC<{
  size: number;
  contentCss?: CSSInterpolation;
}> = ({ size, contentCss }) => (
  <div
    css={[
      contentCss,
      css`
        height: ${size}px;
        width: ${size}px;
      `,
    ]}
  >
    <CloudinaryImg
      cloudinaryId={LOGOS_IMAGE_CLOUDINARY_ID}
      dimensions={{ height: 340, width: 340 }}
      alt="Crypto logos"
    />
  </div>
);

export const GetButtonSection: React.FCC = () => (
  <PageSection contentCss={pageSectionCss}>
    <GridCell>
      <CryptoLogosImg size={LOGOS_IMAGE_SIZE} />
      <TextContainer>
        <SectionAnchor id="add-funds" />
        <h3>Fund your account</h3>
        <p>
          Avoid the hassle and network fees from making multiple crypto
          donations across all the nonprofits you support.
        </p>
        <p>
          Make one crypto donation to fund your Every.org account balance, then
          easily donate that balance to support as many nonprofits as you want!
        </p>
      </TextContainer>
      <Button
        data-tname="AddFunds__Link"
        role={ButtonRole.PRIMARY}
        size={ButtonSize.MEDIUM}
        onClick={{
          kind: ButtonTargetKind.LINK,
          to: getRoutePath({
            name: ClientRouteName.MY_GIVING_PAYMENT,
            fragment: FUND_ACCOUNT_FRAGMENT_ID,
            format: URLFormat.RELATIVE,
          }),
        }}
      >
        Add funds
      </Button>
    </GridCell>
    <GridCell>
      <div
        css={css`
          width: 100%;
          height: ${STARS_AND_BUTTON_IMAGE_HEIGHT}px;
        `}
      >
        <CloudinaryImg
          cloudinaryId={STARS_AND_BUTTON_IMAGE_CLOUDINARY_ID}
          dimensions={{ width: 1040, height: 400 }}
          alt="Donate crypto button"
        />
      </div>
      <GetButtonText />
    </GridCell>
  </PageSection>
);
